import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import useStepPost from '../../hooks/useStepPost';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import useIraApplicationFundingAmountDispatch from '../../hooks/useIraApplicationFundingAmountDispatch';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import Paragraph from '../Paragraph/Paragraph';
import { FormWrap } from '../FormContainer/FormContainer';
import { parseEnumType } from '../../util/helpers/enumMappers';
import {
  EDIT_IRA_FUNDING_TYPES,
  IRA_APPLICATION_FUNDING_TYPES,
} from '../../util/constants';
import Back from '../Buttons/Back';
import { HeadingSubtitle } from '../Heading';
import CurrencyInput from '../CurrencyInput';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import themeColors from '../../assets/styles/themeColors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';
import Select from '../Fields/Select';
import { hasIraContributionDeadlinePassed } from '../../util/helpers/datesHelpers';
import {
  CURRENT_YEAR_OPTION,
  PAST_N_CURRENT_YEAR,
} from '../../constants/registrationConstants';

const FundRetirementAmountForm = ({
  nextPath,
  onModalClose,
  handleBack,
  skipPreviousStep,
  editFundFlow,
  handleNext,
}) => {
  const { t } = useTranslation();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const {
    iraApplicationFundingAmountDispatch,
  } = useIraApplicationFundingAmountDispatch();
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const ApplicantData = useApplicantData();
  const [title, setTitle] = useState('');

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: `Fund your account - ${parseEnumType(
        IRA_APPLICATION_FUNDING_TYPES,
        IraApplicationFundingResponse?.IRAFundingType,
      )}`,
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const isCashContribution =
    parseEnumType(
      IRA_APPLICATION_FUNDING_TYPES,
      IraApplicationFundingResponse?.IRAFundingType,
    ) === parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 3);

  const isRollover =
    parseEnumType(
      IRA_APPLICATION_FUNDING_TYPES,
      IraApplicationFundingResponse?.IRAFundingType,
    ) === parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 2);

  const isIraTransfer =
    parseEnumType(
      IRA_APPLICATION_FUNDING_TYPES,
      IraApplicationFundingResponse?.IRAFundingType,
    ) === parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 1);

  const { RegistrationContributionLimitAmount } = useSelector(selectSettings);

  useEffect(() => {
    const checkInvestmentType = () => {
      if (isRollover) {
        return 'rollover';
      }
      if (isCashContribution) {
        return 'cash';
      }
      if (isIraTransfer) {
        return 'transfer from exising IRA';
      }
    };
    setTitle(t('fundAccount.amountSubtitle', { type: checkInvestmentType() }));
  }, [isRollover, isCashContribution, isIraTransfer, t]);

  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: ['FundAmount'],
      ...(isCashContribution && {
        maxAmount: RegistrationContributionLimitAmount,
      }),
      ...(isRollover && {
        type: 'by rollover',
        maxAmount: 500000000,
      }),
      ...(isIraTransfer && {
        type: 'transfer from existing ira',
        maxAmount: 500000000,
      }),
    }),
  });

  let defaultContributionYear = PAST_N_CURRENT_YEAR.find(
    (year) =>
      year.value ===
      IraApplicationFundingResponse?.ContributionYear?.toString(),
  );
  if (!defaultContributionYear) {
    defaultContributionYear = CURRENT_YEAR_OPTION;
  }

  const defaultValues = {
    FundAmount: IraApplicationFundingResponse?.Amount,
    ContributionYear: defaultContributionYear,
  };

  const { handleSubmit, control, setError } = useForm({
    resolver: yupResolver(formValidationSchema),
    defaultValues,
  });

  const getOnSuccess = () => {
    handleGtmEvent();
    if (editFundFlow) {
      if (
        parseEnumType(
          IRA_APPLICATION_FUNDING_TYPES,
          IraApplicationFundingResponse.IRAFundingType,
        ) === parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 1) &&
        editFundFlow === EDIT_IRA_FUNDING_TYPES.fundingType
      ) {
        if (handleNext) {
          return handleNext;
        }
      }

      if (onSuccess) {
        return onSuccess;
      }
    }

    if (handleNext) {
      return handleNext;
    }

    if (onSuccess) {
      return onSuccess;
    }
  };

  const onSubmit = ({ FundAmount, ContributionYear }) => {
    const onError = (errorMessage) => {
      setError('FundAmount', errorMessage);
    };

    iraApplicationFundingAmountDispatch({
      data: {
        FundAmount,
        ContributionYear: ContributionYear.value,
      },
      defaultValues,
      onSuccess: getOnSuccess(),
      onError,
    });
  };

  const isPastDeadline = hasIraContributionDeadlinePassed();

  return (
    <>
      {parseEnumType(
        IRA_APPLICATION_FUNDING_TYPES,
        IraApplicationFundingResponse.IRAFundingType,
      ) === parseEnumType(IRA_APPLICATION_FUNDING_TYPES, 1) && (
        <Back
          onClick={() => {
            if (editFundFlow === EDIT_IRA_FUNDING_TYPES.fundingAmount) {
              skipPreviousStep();
            } else {
              handleBack();
            }
          }}
        />
      )}

      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {isIraTransfer
          ? t('fundAccount.transferAmountTitle')
          : t('fundAccount.amountTitle')}
      </HeadingSubtitle>
      <Paragraph mb={32} color={themeColors.colorTextXLLight} weight={400}>
        {title}
      </Paragraph>
      <FormWrap>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <CurrencyInput
              label={
                isIraTransfer
                  ? t('fundAccount.amountTransferFieldLabel')
                  : t('fundAccount.amountFieldLabel')
              }
              {...field}
              {...fieldState}
              autoFocus
            />
          )}
          name="FundAmount"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <Select
              label={t('fields.labels.contributionYear')}
              options={
                isPastDeadline ? CURRENT_YEAR_OPTION : PAST_N_CURRENT_YEAR
              }
              {...field}
              {...fieldState}
              autoFocus
              defaultValue={defaultContributionYear}
            />
          )}
          name="ContributionYear"
        />
        <Paragraph color={themeColors.colorTextLight} fontWeight={600}>
          {t('fields.labels.contributionYearSubtext1')}
          <u>{t('fields.labels.contributionYearSubtext2')}</u>
          {t('fields.labels.contributionYearSubtext3')}
          <u>{t('fields.labels.contributionYearSubtext4')}</u>
          {t('fields.labels.contributionYearSubtext5')}
        </Paragraph>
        <OnBoardingCta
          type="submit"
          onClick={handleSubmit(onSubmit)}
          title={t('continue')}
        />
      </FormWrap>
    </>
  );
};

FundRetirementAmountForm.propTypes = {
  onModalClose: PropTypes.func,
  nextPath: PropTypes.string,
  handleBack: PropTypes.func,
  skipPreviousStep: PropTypes.func,
  editFundFlow: PropTypes.string,
  handleNext: PropTypes.func,
};

export default FundRetirementAmountForm;
